interface UptimeDay {
  date: Date;
  status: "operational" | "degraded" | "incident";
}

function generateUptimeDays(): UptimeDay[] {
  const days: UptimeDay[] = [];
  const today = new Date();
  for (let i = 89; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    // Simulate 2 minor incidents in the last 90 days
    const dayIndex = 89 - i;
    let status: UptimeDay["status"] = "operational";
    if (dayIndex === 43) status = "degraded";
    if (dayIndex === 71) status = "degraded";
    days.push({ date: d, status });
  }
  return days;
}

const UPTIME_DAYS = generateUptimeDays();

const STATUS_META = {
  operational: { label: "Operativo", color: "text-emerald-600", bg: "bg-emerald-500", dot: "bg-emerald-400", ring: "ring-emerald-200" },
  degraded:    { label: "Degradado", color: "text-amber-600",   bg: "bg-amber-400",   dot: "bg-amber-400",   ring: "ring-amber-200"   },
  incident:    { label: "Incidente", color: "text-rose-600",    bg: "bg-rose-500",    dot: "bg-rose-400",    ring: "ring-rose-200"    },
};

interface ServiceStatusWidgetProps {
  isEn: boolean;
  activeSince: string;
}

export default function ServiceStatusWidget({ isEn, activeSince }: ServiceStatusWidgetProps) {
  const uptimePercent = (UPTIME_DAYS.filter((d) => d.status === "operational").length / 90 * 100).toFixed(2);
  const currentStatus: UptimeDay["status"] = "operational";
  const meta = STATUS_META[currentStatus];

  const memberStart = new Date(activeSince);
  const diffDays = Math.floor((Date.now() - memberStart.getTime()) / (1000 * 60 * 60 * 24));
  const uptimeDaysSince = Math.max(0, diffDays);

  const COMPONENTS = isEn
    ? [
        { name: "AI Receptionist Engine", status: "operational" as const },
        { name: "Call forwarding infrastructure", status: "operational" as const },
        { name: "Canvas Memory", status: "operational" as const },
        { name: "Calendar integration", status: "operational" as const },
        { name: "Call summary emails", status: "operational" as const },
      ]
    : [
        { name: "Motor de Recepcionista IA", status: "operational" as const },
        { name: "Infraestructura de desvío de llamadas", status: "operational" as const },
        { name: "Canvas Memory", status: "operational" as const },
        { name: "Integración de calendario", status: "operational" as const },
        { name: "Emails de resumen de llamadas", status: "operational" as const },
      ];

  const INCIDENTS = isEn
    ? [
        { date: "Feb 6, 2026", text: "Minor response delay in voice pipeline — resolved in 18 min." },
        { date: "Jan 9, 2026", text: "Canvas Memory latency spike — resolved in 22 min." },
      ]
    : [
        { date: "6 Feb 2026", text: "Ligero retraso en la canalización de voz — resuelto en 18 min." },
        { date: "9 Ene 2026", text: "Pico de latencia en Canvas Memory — resuelto en 22 min." },
      ];

  return (
    <div className="bg-white border border-stone-100 rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="px-6 py-5 border-b border-stone-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${meta.bg} bg-opacity-15`} style={{ backgroundColor: currentStatus === "operational" ? "#d1fae5" : "#fef3c7" }}>
            <i className="ri-shield-check-line text-emerald-600 text-lg" />
          </div>
          <div>
            <h2 className="text-sm font-bold text-stone-800">
              {isEn ? "Service status" : "Estado del servicio"}
            </h2>
            <p className="text-xs text-stone-400">
              {isEn ? "Real-time status of all Auria components" : "Estado en tiempo real de todos los componentes de Auria"}
            </p>
          </div>
        </div>

        {/* Overall status badge */}
        <div className={`flex items-center gap-2 px-4 py-2 rounded-xl border ${currentStatus === "operational" ? "bg-emerald-50 border-emerald-100" : "bg-amber-50 border-amber-100"}`}>
          <span className={`relative flex h-2.5 w-2.5`}>
            <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${meta.dot}`} />
            <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${meta.dot}`} />
          </span>
          <span className={`text-sm font-bold ${meta.color}`}>
            {isEn
              ? currentStatus === "operational" ? "All systems operational" : "Partial degradation"
              : currentStatus === "operational" ? "Todos los sistemas operativos" : "Degradación parcial"}
          </span>
        </div>
      </div>

      <div className="px-6 py-5 space-y-6">
        {/* Uptime stats row */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-stone-50 rounded-xl px-4 py-3 text-center border border-stone-100">
            <p className="text-2xl font-black text-emerald-600">{uptimePercent}%</p>
            <p className="text-xs text-stone-400 mt-0.5">{isEn ? "Uptime (90 days)" : "Uptime (90 días)"}</p>
          </div>
          <div className="bg-stone-50 rounded-xl px-4 py-3 text-center border border-stone-100">
            <p className="text-2xl font-black text-stone-800">&lt; 1s</p>
            <p className="text-xs text-stone-400 mt-0.5">{isEn ? "Avg. response time" : "Tiempo de respuesta"}</p>
          </div>
          <div className="bg-stone-50 rounded-xl px-4 py-3 text-center border border-stone-100">
            <p className="text-2xl font-black text-stone-800">
              {uptimeDaysSince > 0 ? uptimeDaysSince : "—"}
            </p>
            <p className="text-xs text-stone-400 mt-0.5">{isEn ? "Days active for you" : "Días activo para ti"}</p>
          </div>
        </div>

        {/* 90-day uptime bars */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-bold text-stone-500 uppercase tracking-wider">
              {isEn ? "90-day availability" : "Disponibilidad 90 días"}
            </p>
            <span className="text-xs text-stone-400">
              {isEn ? "Today" : "Hoy"}
            </span>
          </div>
          <div className="flex items-end gap-0.5 h-8">
            {UPTIME_DAYS.map((day, i) => (
              <div
                key={i}
                title={`${day.date.toLocaleDateString(isEn ? "en-US" : "es-ES", { day: "numeric", month: "short" })} — ${
                  day.status === "operational"
                    ? (isEn ? "Operational" : "Operativo")
                    : day.status === "degraded"
                    ? (isEn ? "Degraded performance" : "Rendimiento degradado")
                    : (isEn ? "Incident" : "Incidente")
                }`}
                className={`flex-1 rounded-sm cursor-default transition-opacity hover:opacity-70 ${
                  day.status === "operational"
                    ? "bg-emerald-400 h-full"
                    : day.status === "degraded"
                    ? "bg-amber-400 h-3/4"
                    : "bg-rose-400 h-1/2"
                }`}
              />
            ))}
          </div>
          <div className="flex items-center gap-4 mt-2">
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-sm bg-emerald-400 inline-block" />
              <span className="text-xs text-stone-400">{isEn ? "Operational" : "Operativo"}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-sm bg-amber-400 inline-block" />
              <span className="text-xs text-stone-400">{isEn ? "Degraded" : "Degradado"}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-sm bg-rose-400 inline-block" />
              <span className="text-xs text-stone-400">{isEn ? "Incident" : "Incidente"}</span>
            </div>
          </div>
        </div>

        {/* Component breakdown */}
        <div>
          <p className="text-xs font-bold text-stone-500 uppercase tracking-wider mb-3">
            {isEn ? "Components" : "Componentes"}
          </p>
          <div className="space-y-2">
            {COMPONENTS.map((c) => (
              <div
                key={c.name}
                className="flex items-center justify-between py-2.5 px-3 bg-stone-50 rounded-xl border border-stone-100"
              >
                <div className="flex items-center gap-2.5">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-checkbox-circle-fill text-emerald-500 text-sm" />
                  </div>
                  <span className="text-xs font-medium text-stone-700">{c.name}</span>
                </div>
                <span className="text-xs font-semibold text-emerald-600">
                  {isEn ? "Operational" : "Operativo"}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Recent incidents */}
        <div>
          <p className="text-xs font-bold text-stone-500 uppercase tracking-wider mb-3">
            {isEn ? "Recent incidents (last 90 days)" : "Incidentes recientes (últimos 90 días)"}
          </p>
          <div className="space-y-2">
            {INCIDENTS.map((inc, i) => (
              <div
                key={i}
                className="flex items-start gap-3 py-2.5 px-3 bg-amber-50 rounded-xl border border-amber-100"
              >
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-error-warning-line text-amber-500 text-sm" />
                </div>
                <div>
                  <p className="text-xs font-bold text-amber-700">{inc.date}</p>
                  <p className="text-xs text-amber-600 mt-0.5">{inc.text}</p>
                </div>
                <span className="ml-auto text-xs font-semibold text-emerald-600 whitespace-nowrap">
                  {isEn ? "Resolved" : "Resuelto"}
                </span>
              </div>
            ))}
            {INCIDENTS.length === 0 && (
              <div className="py-4 text-center text-xs text-stone-400">
                {isEn ? "No incidents in the last 90 days" : "Sin incidentes en los últimos 90 días"}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
